import type { ReactNode } from "react";
import { api, type Health as HealthData, type JobHealth, type SourceHealth } from "../api";
import { useAsync } from "../useAsync";
import { StatusChip } from "../components/StatusChip";
import { count, money, shortTime, DASH } from "../format";

/**
 * Pipeline answers one question: is the money layer actually working right
 * now? Not "are the integrations configured" — that's Integrations — and not
 * "is the product healthy" — that's the per-product Health audit. This is
 * whether jobs ran, data arrived, and the numbers elsewhere can be trusted.
 */
export function Pipeline() {
  const health = useAsync(() => api.health(), []);

  if (health.error) return <Problem message={health.error} />;
  if (health.loading || !health.data) return <Loading />;
  const h = health.data;

  const failing = h.jobs.filter((j) => j.last_ok === false);
  const neverRan = h.jobs.length === 0;

  return (
    <div style={{ marginTop: 18 }}>
      <Verdict failing={failing.length} neverRan={neverRan} queue={h.queue} />

      <Section title="Jobs" note="A job that never ran shows nothing — that is itself the finding.">
        {h.jobs.length === 0 ? (
          <Empty>
            No job has ever recorded a run. Start the runner with <code>npm run jobs</code>, or
            check that the worker process is up on Fly.
          </Empty>
        ) : (
          <table className="ptable">
            <thead>
              <tr>
                <th>Job</th>
                <th>Last run</th>
                <th>Result</th>
                <th>Last success</th>
                <th>Consecutive failures</th>
              </tr>
            </thead>
            <tbody>
              {h.jobs.map((j) => (
                <JobRow key={j.name} job={j} />
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Sources"
        note="“Latest fact” is when the thing happened; “ingested” is when we learned about it. A wide gap is a lagging sync, not missing data."
      >
        {h.sources.length === 0 ? (
          <Empty>
            No events, transactions, or spend records yet. Until a source is wired, every
            figure in this dashboard is zero because there is nothing to count — not because
            the numbers are bad.
          </Empty>
        ) : (
          <table className="ptable">
            <thead>
              <tr>
                <th>Table</th>
                <th>Source</th>
                <th>Latest fact</th>
                <th>Ingested</th>
                <th>Rows 7d</th>
                <th>Rows total</th>
              </tr>
            </thead>
            <tbody>
              {h.sources.map((s) => (
                <SourceRow key={`${s.fact}:${s.source}`} s={s} />
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Attribution"
        note="Revenue with no identifiable first touch, per project, last 30 days. Never divided across channels."
      >
        <table className="ptable">
          <thead>
            <tr>
              <th>Project</th>
              <th>Revenue 30d</th>
              <th>Unattributed</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            {h.attribution.map((a) => {
              const total = Number(a.revenue_30d_cents);
              const un = Number(a.unattributed_30d_cents);
              const share = total > 0 ? un / total : null;
              return (
                <tr key={a.product_slug} style={{ cursor: "default" }}>
                  <td>
                    <div className="cell">{a.product_slug}</div>
                  </td>
                  <td>
                    <div className="cell num">{money(total)}</div>
                  </td>
                  <td>
                    <div className="cell num">{money(un)}</div>
                  </td>
                  <td>
                    <div
                      className="cell num"
                      style={{
                        color:
                          share === null
                            ? "var(--text-faint)"
                            : share > 0.4
                              ? "var(--warn)"
                              : "var(--text-2)",
                      }}
                    >
                      {share === null ? DASH : `${(share * 100).toFixed(0)}%`}
                    </div>
                  </td>
                </tr>
              );
            })}
            {h.attribution.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <div className="cell" style={{ color: "var(--text-4)" }}>
                    No live projects.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Section>

      <div className="banner" style={{ marginTop: 26 }}>
        <span>
          Read at {shortTime(h.now)}. This page does not poll — use Refresh in the header.
        </span>
      </div>
    </div>
  );
}

function Verdict({
  failing,
  neverRan,
  queue,
}: {
  failing: number;
  neverRan: boolean;
  queue: HealthData["queue"];
}) {
  const items: Array<{ label: string; value: string; tone: string }> = [
    {
      label: "Jobs failing",
      value: neverRan ? DASH : String(failing),
      tone: neverRan ? "var(--text-faint)" : failing > 0 ? "var(--neg)" : "var(--pos)",
    },
    {
      label: "Queue pending",
      value: queue ? String(queue.pending) : DASH,
      tone: queue && queue.pending > 0 ? "var(--warn)" : "var(--text)",
    },
    {
      label: "Past expiry",
      value: queue ? String(queue.past_expiry) : DASH,
      tone: queue && queue.past_expiry > 0 ? "var(--warn)" : "var(--text)",
    },
    {
      label: "Oldest pending",
      value: queue?.oldest_pending_at ? age(queue.oldest_pending_at) : DASH,
      tone: "var(--text)",
    },
  ];

  return (
    <div className="metrics" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
      {items.map((m) => (
        <div key={m.label} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <div className="eyebrow">{m.label}</div>
          <div className="num" style={{ fontSize: 25, letterSpacing: "-0.03em", color: m.tone }}>
            {m.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function JobRow({ job }: { job: JobHealth }) {
  const ok = job.last_ok;
  return (
    <tr style={{ cursor: "default" }}>
      <td>
        <div className="cell">{job.name}</div>
      </td>
      <td>
        <div className="cell num">{age(job.last_started_at)}</div>
      </td>
      <td>
        <div className="cell" style={{ textAlign: "right" }}>
          {ok === null ? (
            <span style={{ color: "var(--text-faint)" }}>running</span>
          ) : ok ? (
            <StatusChip kind="passing" label="OK" />
          ) : (
            <span title={job.last_error ?? undefined}>
              <StatusChip kind="failing" label="Failed" />
            </span>
          )}
        </div>
      </td>
      <td>
        <div
          className="cell num"
          style={{ color: job.last_success_at ? "var(--text-2)" : "var(--neg)" }}
        >
          {job.last_success_at ? age(job.last_success_at) : "never"}
        </div>
      </td>
      <td>
        <div
          className="cell num"
          style={{ color: job.consecutive_failures > 0 ? "var(--neg)" : "var(--text-2)" }}
        >
          {job.consecutive_failures}
        </div>
      </td>
    </tr>
  );
}

function SourceRow({ s }: { s: SourceHealth }) {
  // Anything that hasn't received a row in 48h is worth a second look; the job
  // schedule is hourly for MySQL and daily for everything else.
  const stale =
    !s.latest_ingest_at || Date.now() - new Date(s.latest_ingest_at).getTime() > 48 * 3_600_000;
  return (
    <tr style={{ cursor: "default" }}>
      <td>
        <div className="cell" style={{ color: "var(--text-3)" }}>
          {s.fact}
        </div>
      </td>
      <td>
        <div className="cell" style={{ textAlign: "right" }}>
          {s.source}
        </div>
      </td>
      <td>
        <div className="cell num">{age(s.latest_fact_at)}</div>
      </td>
      <td>
        <div className="cell num" style={{ color: stale ? "var(--warn)" : "var(--text-2)" }}>
          {age(s.latest_ingest_at)}
        </div>
      </td>
      <td>
        <div className="cell num">{count(s.rows_7d)}</div>
      </td>
      <td>
        <div className="cell num" style={{ color: "var(--text-3)" }}>
          {count(s.rows_total)}
        </div>
      </td>
    </tr>
  );
}

/** "12m ago" / "3d ago" / "never". Absolute timestamps sit in the title attribute. */
function age(iso: string | null): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return DASH;
  const mins = Math.round((Date.now() - t) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section style={{ marginTop: 34 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
        <h2>{title}</h2>
        {note && (
          <span className="sub" style={{ maxWidth: 720 }}>
            {note}
          </span>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>{children}</div>
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        border: "1px dashed var(--border-strong)",
        borderRadius: 12,
        padding: "20px 22px",
        fontSize: 13,
        lineHeight: 1.6,
        color: "var(--text-3)",
        background: "var(--surface)",
      }}
    >
      {children}
    </div>
  );
}

function Loading() {
  return (
    <div style={{ color: "var(--text-faint)", fontSize: 13, marginTop: 20 }}>
      Loading pipeline health…
    </div>
  );
}

function Problem({ message }: { message: string }) {
  return (
    <div className="form-error" style={{ marginTop: 20 }}>
      {message}
    </div>
  );
}
